import { useHomeTabs } from '../../queries/useHomeTabs';
import { TabView } from 'react-native-tab-view';
import { useEffect, useMemo, useState } from 'react';
import { Typography, useScreenSize } from '@banhala/ui-kit';
import { useSetAtom } from 'jotai';
import {
  activeHomeTabAtom,
  type ActiveHomeTab,
} from '../../states/homeTab.atom';
import type { HomeTabItem } from 'shared/src/api';
import { HomeAtomic } from '../HomeAtomic';
import { HomeWebView } from '../HomeWebView';
import { TodayAtomic } from '../TodayAtomic';
import { GoodsRanking } from '../GoodsRanking';
import { MarketRanking } from '../MarketRanking';
import { HomeTabBar } from './components/HomeTabBar';
import { isWeb } from 'shared/src/utils';

export type HomeTabRoute = {
  key: string;
  title: string;
} & HomeTabItem;

export const HomeContents = () => {
  const { data: homeTabs } = useHomeTabs();
  const initialIndex = homeTabs.item_list.findIndex(
    item => item.id === homeTabs.selected_id,
  );
  const [index, setIndex] = useState(initialIndex);
  const size = useScreenSize();
  const setActiveHomeTab = useSetAtom(activeHomeTabAtom);

  const routes = useMemo<HomeTabRoute[]>(() => {
    return (
      homeTabs.item_list?.map(item => ({
        ...item,
        key: item.id,
        title: item.name,
      })) ?? []
    );
  }, [homeTabs.item_list]);

  // 현재 index + routes로부터 활성 탭 메타 도출 → atom 동기화
  const activeTab = useMemo<ActiveHomeTab | null>(() => {
    const current = routes[index];
    if (!current) return null;
    return {
      id: current.id,
      screen_name: current.screen_name,
      type: current.type,
      name: current.name,
    };
  }, [routes, index]);

  useEffect(() => {
    setActiveHomeTab(activeTab);
  }, [activeTab, setActiveHomeTab]);

  if (homeTabs.item_list.length === 0) return null;

  return (
    <TabView<HomeTabRoute>
      lazy={true}
      navigationState={{ index, routes }}
      renderScene={item => {
        if (item.route.type === 'ATOMIC') {
          return (
            <HomeAtomic
              screenName={item.route.screen_name}
              nextToken={item.route.default_query?.next_token}
            />
          );
        }

        if (item.route.type === 'WEBVIEW') {
          return <HomeWebView url={item.route.default_query?.url} />;
        }

        if (item.route.type === 'CUSTOM') {
          if (item.route.screen_name === 'TODAY') {
            return <TodayAtomic screenName={item.route.screen_name} />;
          }

          if (item.route.screen_name === 'GOODS_RANKING') {
            return <GoodsRanking />;
          }

          if (item.route.screen_name === 'MARKET_RANKING') {
            return <MarketRanking />;
          }
        }
        return <Typography>{item.route.title}</Typography>;
      }}
      onIndexChange={setIndex}
      initialLayout={{
        width: size.width,
      }}
      swipeEnabled={!isWeb}
      renderTabBar={props => <HomeTabBar {...props} />}
    />
  );
};
